import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { groupBg } from "@/lib/lineItems";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import PrintExportButtons from "@/components/PrintExportButtons";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Build one card per package group + one for ungrouped items.
function buildPackages(request) {
  const items = request.items || [];
  const map = new Map();
  items.forEach((it) => {
    const key = it.package_group ? String(it.package_group) : "_none";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  });
  return [...map.entries()]
    .sort(([a], [b]) => (a === "_none" ? 1 : b === "_none" ? -1 : Number(a) - Number(b)))
    .map(([key, list]) => ({
      key,
      label: key === "_none" ? "Loose Items" : `Package ${key}`,
      color: key === "_none" ? "bg-zinc-700" : groupBg(key),
      items: list,
      totalQty: list.reduce((s, it) => s + (Number(it.quantity) || 0), 0),
      totalWeight: list.reduce((s, it) => s + (Number(it.weight) || 0), 0),
    }));
}

export default function PackageLabels() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [r, setR] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/requests/${id}`);
      setR(data);
    })();
  }, [id]);

  // Auto-fire action from ?action=print|pdf|download once the request has loaded.
  useEffect(() => {
    if (!r) return;
    const action = sp.get("action");
    if (!action) return;
    const t = setTimeout(async () => {
      if (action === "print") {
        window.print();
      } else if (action === "download") {
        try {
          const node = printRef.current || document.body;
          const html = `<!doctype html><html><head><meta charset="utf-8"><title>labels-${r.reference_number}</title>` +
            Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
              .map((n) => n.outerHTML).join("") +
            `</head><body>${node.outerHTML}</body></html>`;
          const blob = new Blob([html], { type: "text/html;charset=utf-8" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `labels-${r.reference_number}.html`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch {
          toast.error("Download failed");
        }
      } else if (action === "pdf") {
        try {
          const html2pdf = (await import("html2pdf.js")).default;
          const node = printRef.current || document.body;
          await html2pdf().from(node).set({
            margin: [10, 10],
            filename: `labels-${r.reference_number}.pdf`,
            html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["css", "legacy"] },
          }).save();
        } catch {
          toast.error("PDF export failed");
        }
      }
    }, 400);
    return () => clearTimeout(t);
  }, [r, sp]);

  if (!r) return <div className="p-10 text-sm text-muted-foreground">Loading labels…</div>;

  const packs = buildPackages(r);

  return (
    <div className="min-h-screen bg-muted/40 print:bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .page-break { page-break-after: always; break-after: page; }
          body { background: white; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-background border-b-2 border-border px-4 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogoMark size={30} className="text-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Out &amp; In &middot; Print Labels</div>
            <div className="font-mono font-bold text-sm">{r.reference_number}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => window.close()} variant="outline" data-testid="close-labels" className="rounded-none border-2 h-9 uppercase tracking-[0.15em] text-xs font-bold">
            <X className="h-3.5 w-3.5 mr-2" /> Close
          </Button>
          <PrintExportButtons
            targetRef={printRef}
            fileName={`labels-${r.reference_number}`}
            testidPrefix="labels-export"
          />
        </div>
      </div>

      <div ref={printRef} className="p-6 sm:p-10 space-y-6 print:p-0 print:space-y-0">
        {packs.length === 0 && (
          <div className="text-sm text-muted-foreground">This request has no line items to label.</div>
        )}
        {packs.map((p, idx) => (
          <div
            key={p.key}
            data-testid={`label-card-${p.key}`}
            className={`bg-white border-2 border-black max-w-[720px] mx-auto ${idx < packs.length - 1 ? "page-break" : ""}`}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-black text-white">
              <div className="flex items-center gap-3">
                <LogoMark size={36} className="text-white" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 font-bold">Out &amp; In Dispatch</div>
                  <div className="font-mono text-lg font-black tracking-tight">{r.reference_number}</div>
                </div>
              </div>
              {r.created_by_company_logo_url && (
                <div className="mx-4 flex items-center gap-3 border-l border-white/20 pl-4">
                  <img
                    data-testid="label-client-logo"
                    src={r.created_by_company_logo_url.startsWith("http") ? r.created_by_company_logo_url : `${BACKEND}${r.created_by_company_logo_url}`}
                    alt={r.created_by_company_name || "Client logo"}
                    className="max-h-10 max-w-[120px] object-contain bg-white p-1"
                  />
                  {r.created_by_company_name && (
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/70">{r.created_by_company_name}</div>
                  )}
                </div>
              )}
              <div className={`px-3 py-2 text-white font-bold text-lg uppercase tracking-wider ${p.color}`}>
                {p.label}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-0 border-b-2 border-black">
              <div className="p-4 border-r-2 border-black">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">From</div>
                <div className="font-bold text-sm mt-1">{r.pickup?.company}</div>
                <div className="text-xs text-muted-foreground">{r.pickup?.contact_name}</div>
                <div className="text-xs whitespace-pre-line">{r.pickup?.address}</div>
                <div className="font-mono text-[11px] mt-1">{r.pickup?.phone}</div>
              </div>
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-orange-600">To</div>
                <div className="font-bold text-sm mt-1">{r.delivery?.company}</div>
                <div className="text-xs text-muted-foreground">{r.delivery?.contact_name}</div>
                <div className="text-xs whitespace-pre-line">{r.delivery?.address}</div>
                <div className="font-mono text-[11px] mt-1">{r.delivery?.phone}</div>
              </div>
            </div>

            <div className="p-4 border-b-2 border-black text-xs">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <div><span className="uppercase tracking-wider text-muted-foreground font-bold">Courier:</span> {r.courier?.company || "—"}</div>
                <div><span className="uppercase tracking-wider text-muted-foreground font-bold">Priority:</span> {(r.priority || "standard").toUpperCase()}</div>
                <div><span className="uppercase tracking-wider text-muted-foreground font-bold">Type:</span> {(r.request_type || "").toUpperCase()}</div>
              </div>
              {(r.references?.po_number || r.references?.invoice_number || r.references?.job_number) && (
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1">
                  {r.references?.po_number && <div><span className="uppercase tracking-wider text-muted-foreground font-bold">PO:</span> {r.references.po_number}</div>}
                  {r.references?.invoice_number && <div><span className="uppercase tracking-wider text-muted-foreground font-bold">INV:</span> {r.references.invoice_number}</div>}
                  {r.references?.job_number && <div><span className="uppercase tracking-wider text-muted-foreground font-bold">JOB:</span> {r.references.job_number}</div>}
                </div>
              )}
            </div>

            <div className="p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground mb-2">Contents</div>
              <table className="w-full text-xs">
                <tbody>
                  {p.items.map((it, i) => (
                    <tr key={i} className="border-b border-dashed border-black/20">
                      <td className="py-1.5 font-mono w-16">{it.item_number || "—"}</td>
                      <td className="py-1.5">
                        {it.description}
                        {it.dimensions && <span className="ml-2 text-[10px] font-mono uppercase text-muted-foreground">({it.dimensions})</span>}
                      </td>
                      <td className="py-1.5 text-right font-mono w-14">{it.quantity}</td>
                      <td className="py-1.5 text-right w-20 text-muted-foreground">{it.package_type || ""}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td colSpan={2} className="pt-2 uppercase tracking-wider text-[10px]">Totals</td>
                    <td className="pt-2 text-right font-mono">{p.totalQty}</td>
                    <td className="pt-2 text-right font-mono">{p.totalWeight ? `${p.totalWeight.toFixed(2)} kg` : "—"}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">
                Handled by {r.created_by_name || "requestor"} &middot; {new Date(r.created_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
